// @givmo/fundraisers-module — a game's bonus pledges' calls (FCM-WEB35, D-FCM-77). The committed OpenAPI
// snapshot carries none of the four routes the tab acts through: the backend's one act (FCM-BE180,
// `POST /fundraisers/{game_id}/campaign/bonus-pledges`) and Gamified Fundraisers' offer close, early end and
// count reports. So they use the configured `givmoClient` (`api-client.md` § A whole ROUTE the snapshot lacks)
// and inherit the auth, App Check and error middleware. The sentence preview is the generated op; its NAMED key
// (`condition_sentence`, FCM-BE181) is read by hand off its answer (`bonus-pledges-decode.ts`).
import { givmoClient, idempotencyKey, sdk, type PledgeOfferCreate } from '@givmo/api-client';

/** The act's body (`P2PBonusPledgeRequest`): a moment the staff settle, or the game's own score reaching a
 *  number. `charity_id` only where the game's pages pick theirs (a company's season, from its list). */
export type BonusPledgeBody =
  | { kind: 'moment'; moment: string; amount_cents: number; charity_id?: string }
  | {
      kind: 'count';
      measure_id: string;
      at_least: number;
      amount_cents: number;
      charity_id?: string;
    };

/** "Add bonus pledge": its fundraiser under the game, its yes/no for a moment, and its offer, in one act.
 *  The key is the press’s; repeated under it, the server answers the bonus pledge made the first time. */
export async function addBonusPledge(
  gameId: string,
  body: BonusPledgeBody,
  key: string,
): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `/fundraisers/${encodeURIComponent(gameId)}/campaign/bonus-pledges`,
    body,
    ...idempotencyKey(key),
    throwOnError: true,
  });
  return data;
}

/** "Stop pledging": pledging on the offer ends now; pledges already made stand (GFU-BE20's close). */
export async function stopBonusPledging(offerId: string, key: string): Promise<unknown> {
  const { data } = await givmoClient.post({
    url: `/pledge-offers/${encodeURIComponent(offerId)}/close`,
    ...idempotencyKey(key),
    throwOnError: true,
  });
  return data;
}

/**
 * "Lock result" on a moment (decision 4): what the server takes, in its order, each under its own key derived
 * from the press's, so a press repeated replays each step that already landed. While its yes/no still counts,
 * its early end first (GFU-BE16: it also stops pledging on it), then its final and its lock through the one
 * count-report write (GFU-BE18), 1 for "It happened", 0 for "It didn't".
 */
export async function lockMomentResult(
  measureId: string,
  happened: boolean,
  counting: boolean,
  key: string,
): Promise<void> {
  const url = `/pledge-campaigns/${encodeURIComponent(measureId)}`;
  if (counting)
    await givmoClient.post({
      url: `${url}/end-early`,
      ...idempotencyKey(`${key}-end`),
      throwOnError: true,
    });
  for (const stage of ['final', 'confirmed'] as const)
    await givmoClient.post({
      url: `${url}/count-reports`,
      body: { count: happened ? '1' : '0', stage },
      ...idempotencyKey(`${key}-${stage}`),
      throwOnError: true,
    });
}

/** The fans' card's words before anything is written: the creator's preview, with the game as the host. */
export async function previewBonusSentence(
  body: PledgeOfferCreate,
  signal?: AbortSignal,
): Promise<unknown> {
  const { data } = await sdk.pledgesPreviewPledgeOfferRule({ body, signal, throwOnError: true });
  return data;
}
